import moment from "moment";

/**
 * Main transformation function for processing calendar data
 * @param {Array} datesArray - Array of date objects with dayData
 * @returns {Array} - Transformed datesArray with processed data
 */
export function transform(datesArray) {
    const orderProcessMap = {};
    const processedMap = {}; // Track which processes have been fully handled

    // Main transformation flow
    datesArray = cleanAndSortDatesArray(datesArray);
    buildOrderProcessMap(datesArray, orderProcessMap);
    processAndCalculateWidths(datesArray, orderProcessMap, processedMap);
    
    const withDummies = createDummyProcesses(datesArray);
    
    // Assign sort keys
    return assignSortKeys(withDummies);
}

/**
 * Step 1: Filter out unwanted dayData entries and sort by process_width descending
 */
function cleanAndSortDatesArray(dates) {
    return dates.map((dateElem) => {
        const filteredDayData = dateElem.dayData
            .filter(
                (d) =>
                    !("skipForChekedinCheck" in d.proccess) &&
                    !d.proccess?.isProcessCompletedNow &&
                    !("dummy" in d)
            )
            .sort((a, b) => b.proccess.process_width - a.proccess.process_width);

        return { ...dateElem, dayData: filteredDayData };
    });
}

/**
 * Step 2: Build orderProcessMap with earliest start and latest end per order_process_id
 */
function buildOrderProcessMap(dates, orderProcessMap) {
    dates.forEach(({ dayData }) => {
        dayData.forEach(({ order_id, proccess }) => {
            if (!proccess) return;

            const { process_date, process_id } = proccess;
            const process_endDate = proccess.process_endDate || process_date;
            const id = `${order_id}_${process_id}`;

            if (orderProcessMap[id]) {
                if (moment(process_date).isBefore(orderProcessMap[id].start)) {
                    orderProcessMap[id].start = process_date;
                }
                if (moment(process_endDate).isAfter(orderProcessMap[id].end)) {
                    orderProcessMap[id].end = process_endDate;
                }
            } else {
                orderProcessMap[id] = { start: process_date, end: process_endDate };
            }
        });
    });
}

/**
 * Step 3: Remove duplicates and calculate total working-day and visible segment widths
 */
function processAndCalculateWidths(dates, orderProcessMap, processedMap) {
    for (let i = 0; i < dates.length; i++) {
        const dayData = dates[i].dayData;

        for (let j = dayData.length - 1; j >= 0; j--) {
            const dayDataElem = dayData[j];
            const { proccess } = dayDataElem;

            if (!proccess || proccess.skipForChekedinCheck) continue;

            const { order_id, process_id } = proccess;
            const id = `${order_id}_${process_id}`;

            // If already processed, this is a duplicate - remove it
            if (processedMap[id]) {
                dates[i].dayData.splice(j, 1);
                continue;
            }

            // Mark as processed
            processedMap[id] = { dayIndex: i, rowIndex: j };

            const { start, end } = orderProcessMap[id];
            const startDate = moment(start).startOf("day");
            const endDate = moment(end).startOf("day");
            if (!startDate.isValid() || !endDate.isValid()) continue;

            const workingDateIndexes = dates.reduce((indexes, date, index) => {
                const currentDate = moment(date.date).startOf("day");
                if (
                    currentDate.isSameOrAfter(startDate, "day") &&
                    currentDate.isSameOrBefore(endDate, "day") &&
                    !date.dayOff &&
                    date.holiday?.status !== "Day Off"
                ) {
                    indexes.push(index);
                }
                return indexes;
            }, []);

            const startIsVisible = dates.some((date) =>
                moment(date.date).isSame(startDate, "day")
            );
            const endIsVisible = dates.some((date) =>
                moment(date.date).isSame(endDate, "day")
            );
            const calculatedWidth = workingDateIndexes.length;
            const totalWidth = startIsVisible && endIsVisible
                ? calculatedWidth
                : Math.max(Number(proccess.process_width) || 0, calculatedWidth);

            proccess.process_width = totalWidth;

            const segments = [];
            workingDateIndexes.forEach((dateIndex) => {
                const previousSegment = segments[segments.length - 1];
                const sameWeek = previousSegment &&
                    moment(dates[previousSegment.endIndex].date)
                        .isSame(moment(dates[dateIndex].date), "week");

                if (previousSegment && dateIndex === previousSegment.endIndex + 1 && sameWeek) {
                    previousSegment.endIndex = dateIndex;
                    previousSegment.width++;
                } else {
                    segments.push({ startIndex: dateIndex, endIndex: dateIndex, width: 1 });
                }
            });

            if (!segments.length) {
                proccess.display_width = 1;
                continue;
            }

            proccess.display_width = segments[0].width;
            segments.slice(1).forEach((segment, segmentIndex) => {
                const extension = {
                    ...dayDataElem,
                    proccess: {
                        ...proccess,
                        display_width: segment.width,
                        skipForChekedinCheck: true,
                        brokenProcessIndex: `${i}_${j}_${segmentIndex + 1}`,
                        sourceProcessId: id,
                    },
                };
                dates[segment.startIndex].dayData.push(extension);
            });
        }
    }
}

/**
 * Creates dummy processes for multi-day processes to maintain visual continuity
 */
function createDummyProcesses(datesArray) {
    console.log('Creating dummy processes for multi-day processes...', datesArray);
    // Create a snapshot of all real processes with their positions
    const processPositions = []; // Array of {dayIndex, rowIndex, processId, process_width, data}

    for (let i = 0; i < datesArray.length; i++) {
        const { dayData } = datesArray[i];
        for (let j = 0; j < dayData.length; j++) {
            const currentDayData = dayData[j];
            const { order_id, dummy, proccess } = currentDayData;

            if (!proccess || dummy) continue;

            const processId = proccess.sourceProcessId || `${order_id}_${proccess.process_id}`;
            const process_width = proccess.display_width || proccess.process_width || 1;

            if (process_width > 1) {
                processPositions.push({
                    dayIndex: i,
                    rowIndex: j,
                    processId: processId,
                    process_width: process_width,
                    data: currentDayData,
                });
            }
        }
    }

    // Now create dummies based on the snapshot
    for (const { dayIndex, rowIndex, processId, process_width, data } of processPositions) {
        for (let offset = 1; offset < process_width && dayIndex + offset < datesArray.length; offset++) {
            const targetDayIndex = dayIndex + offset;
            const nextDayData = datesArray[targetDayIndex].dayData;

            const dummyProcess = {
                ...data,
                proccess: {
                    ...data.proccess,
                    _id: `dummy_${data.proccess._id}_offset${offset}`,
                },
                dummy: true,
                dummyForProcessId: processId,
                dummyOriginalRow: rowIndex,
            };

            // Find where to insert: try to maintain the same row, but check if something's there
            let insertIndex = rowIndex;
            
            // If the row doesn't exist yet, just push
            if (insertIndex >= nextDayData.length) {
                nextDayData.push(dummyProcess);
            } else {
                // Check if the position is already a dummy for this same process
                const existingAtPosition = nextDayData[insertIndex];
                if (existingAtPosition?.dummyForProcessId === processId) {
                    // Already has dummy for this process, skip
                    continue;
                }
                
                // Insert at the original row position
                nextDayData.splice(insertIndex, 0, dummyProcess);
            }
        }
    }

    return datesArray;
}

/**
 * Assigns sort keys to processes to maintain consistent vertical ordering across days.
 * Uses gap-filling logic to avoid unnecessarily high sort keys.
 */
function assignSortKeys(datesArray) {
    const processIdToSortKey = new Map();
    
    datesArray.forEach((day) => {
        if (!day.dayData || !Array.isArray(day.dayData)) return;
        
        // Collect all process IDs on this day (including dummies)
        const processIdsOnThisDay = new Set();
        day.dayData.forEach(p => {
            if (!p.proccess) return;
            
            // Use sourceProcessId if available, otherwise construct from order_id + process_id
            const processId = p.proccess.sourceProcessId || 
                             `${p.order_id}_${p.proccess.process_id}`;
            
            if (processId) {
                processIdsOnThisDay.add(processId);
            }
        });
        
        // Find which sort keys are already taken by existing processes
        const takenSortKeys = new Set();
        processIdsOnThisDay.forEach(processId => {
            if (processIdToSortKey.has(processId)) {
                takenSortKeys.add(processIdToSortKey.get(processId));
            }
        });
        
        // Assign sort keys to new processes, filling gaps
        let nextSortKey = 1;
        processIdsOnThisDay.forEach(processId => {
            if (!processIdToSortKey.has(processId)) {
                // Find the smallest available sort key
                while (takenSortKeys.has(nextSortKey)) {
                    nextSortKey++;
                }
                processIdToSortKey.set(processId, nextSortKey);
                takenSortKeys.add(nextSortKey);
                nextSortKey++;
            }
        });

        // Apply sort keys to all processes (real and dummy)
        day.dayData.forEach(p => {
            if (!p.proccess) return;
            
            const processId = p.proccess.sourceProcessId || 
                             `${p.order_id}_${p.proccess.process_id}`;
            
            if (processId && processIdToSortKey.has(processId)) {
                p.proccess.sortKey = processIdToSortKey.get(processId);
            }
        });
    });

    return datesArray;
}